/**
 * Team Grid — Bio panel toggle
 *
 * Clicking anywhere on a card opens a full-width bio panel below
 * the row the card lives in. Uses the Web Animations API for smooth
 * height + opacity transitions. An animation lock prevents
 * overlapping state changes.
 */

function initTeamGrids() {
	const grids = document.querySelectorAll( '.corsa-team-grid__grid' );

	if ( ! grids.length ) {
		return;
	}

	const DURATION = 400;
	const EASING   = 'cubic-bezier(0.25, 0.1, 0.25, 1)';

	grids.forEach( ( grid ) => {
		const cards    = Array.from( grid.querySelectorAll( '.corsa-team-grid__card[data-bio-index]' ) );
		const bioPanel = grid.querySelector( '.corsa-team-grid__bio' );

		if ( ! bioPanel || ! cards.length ) {
			return;
		}

		const bioName  = bioPanel.querySelector( '.corsa-team-grid__bio-name' );
		const bioRole  = bioPanel.querySelector( '.corsa-team-grid__bio-role' );
		const bioText  = bioPanel.querySelector( '.corsa-team-grid__bio-text' );
		const bioClose = bioPanel.querySelector( '.corsa-team-grid__bio-close' );

		let activeIndex   = -1;
		let animationBusy = false;

		// ── Animation helpers (WAAPI) ─────────────────────────────────

		// The gap value cards use for vertical spacing. The bio panel
		// animates this as marginBottom so closing doesn't cause a jump.
		const GAP = getComputedStyle( grid ).columnGap || '24px';

		function animateOpen( el ) {
			return new Promise( ( resolve ) => {
				el.classList.add( 'is-open' );
				el.style.overflow = 'hidden';
				el.style.height = '0px';
				el.style.opacity = '0';
				el.style.marginBottom = '0px';

				// Force reflow.
				void el.offsetHeight;

				const height = el.scrollHeight;

				const anim = el.animate(
					[
						{ height: '0px', opacity: 0, marginBottom: '0px' },
						{ height: height + 'px', opacity: 1, marginBottom: GAP },
					],
					{ duration: DURATION, easing: EASING }
				);

				anim.onfinish = () => {
					el.style.height = '';
					el.style.opacity = '';
					el.style.overflow = '';
					el.style.marginBottom = GAP;
					resolve();
				};
			} );
		}

		function animateClose( el ) {
			return new Promise( ( resolve ) => {
				const height = el.scrollHeight;
				el.style.overflow = 'hidden';

				const anim = el.animate(
					[
						{ height: height + 'px', opacity: 1, marginBottom: GAP },
						{ height: '0px', opacity: 0, marginBottom: '0px' },
					],
					{ duration: DURATION, easing: EASING }
				);

				anim.onfinish = () => {
					el.classList.remove( 'is-open' );
					el.style.overflow = '';
					el.style.marginBottom = '';
					resolve();
				};
			} );
		}

		// ── Positioning ───────────────────────────────────────────────

		function positionPanel( card ) {
			const clickedTop = card.getBoundingClientRect().top;
			let lastInRow = card;

			for ( const c of cards ) {
				if ( Math.abs( c.getBoundingClientRect().top - clickedTop ) < 5 ) {
					lastInRow = c;
				}
			}

			lastInRow.after( bioPanel );
		}

		// ── Core logic ────────────────────────────────────────────────

		async function openBio( card ) {
			if ( animationBusy ) {
				return;
			}

			const clickedIndex = parseInt( card.dataset.bioIndex, 10 );

			// Toggle off if clicking the same card.
			if ( activeIndex === clickedIndex && bioPanel.classList.contains( 'is-open' ) ) {
				await closeBio();
				return;
			}

			animationBusy = true;

			// If already open for a different person, close first.
			if ( bioPanel.classList.contains( 'is-open' ) ) {
				await animateClose( bioPanel );
			}

			activeIndex = clickedIndex;

			// Populate content.
			bioName.textContent = card.dataset.bioName || '';
			bioRole.textContent = card.dataset.bioRole || '';
			bioText.innerHTML   = card.dataset.bioText || '';

			// Position after the correct row.
			positionPanel( card );

			// Animate open.
			bioPanel.hidden = false;
			bioPanel.setAttribute( 'aria-hidden', 'false' );
			await animateOpen( bioPanel );

			// Scroll into view.
			bioPanel.scrollIntoView( { behavior: 'smooth', block: 'nearest' } );

			animationBusy = false;
		}

		async function closeBio() {
			if ( animationBusy ) {
				return;
			}
			animationBusy = true;

			await animateClose( bioPanel );

			bioPanel.hidden = true;
			bioPanel.setAttribute( 'aria-hidden', 'true' );
			activeIndex = -1;

			animationBusy = false;
		}

		// ── Event listeners ───────────────────────────────────────────

		grid.addEventListener( 'click', ( e ) => {
			if ( e.target.closest( '.corsa-team-grid__bio' ) ) {
				return;
			}

			const card = e.target.closest( '.corsa-team-grid__card[data-bio-index]' );
			if ( card ) {
				openBio( card );
			}
		} );

		if ( bioClose ) {
			bioClose.addEventListener( 'click', ( e ) => {
				e.stopPropagation();
				closeBio();
			} );
		}

		// Reposition on resize if open.
		let resizeTimer;
		window.addEventListener( 'resize', () => {
			clearTimeout( resizeTimer );
			resizeTimer = setTimeout( () => {
				if ( activeIndex < 0 || ! bioPanel.classList.contains( 'is-open' ) ) {
					return;
				}
				const activeCard = cards[ activeIndex ];
				if ( activeCard ) {
					positionPanel( activeCard );
				}
			}, 150 );
		} );
	} );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initTeamGrids );
} else {
	initTeamGrids();
}
